import React, { useState } from 'react';
import { motion } from 'motion/react';
import { toast } from "sonner";
import { Card, CardContent } from '@/src/components/ui/card';
import { Button } from '@/src/components/ui/button';
import { Input } from '@/src/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/components/ui/select';
import { getSupabaseClient } from '@/src/lib/supabase';
import { X, UserPlus, Loader2 } from 'lucide-react';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  plans: any[];
}

export function AdminCreateMerchantModal({ onClose, onSuccess, plans }: Props) {
  const [loading, setLoading] = useState(false);
  const [selectedModule, setSelectedModule] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    business_name: '',
    password: '',
    plan_id: 'free',
  });

  const uniqueModules = Array.from(new Set(plans.map((p: any) => p.module_name).filter(Boolean)));
  const plansForSelectedModule = plans.filter((p: any) => p.module_name === selectedModule);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!formData.full_name || !formData.email || !formData.business_name) {
      toast.error('Name, Email, and Business Name are required');
      return;
    }
    setLoading(true);
    try {
      const supabase = getSupabaseClient();
      if (!supabase) throw new Error("Supabase client not initialized");
      const { data: { session } } = await supabase.auth.getSession();

      const payload: any = { ...formData, module_name: selectedModule };
      if (payload.plan_id === 'free') delete payload.plan_id;

      const response = await fetch('/api/admin-create-merchant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to create merchant');
      }

      toast.success('Merchant created successfully!');
      onSuccess();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create merchant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh]"
      >
        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900 shrink-0">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            Manually Create Merchant
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          <form id="create-merchant-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium dark:text-slate-300">Owner Name *</label>
                <Input required placeholder="e.g. Rahul Sharma" value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} className="dark:bg-slate-800 dark:border-slate-700" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium dark:text-slate-300">Email Address *</label>
                <Input required type="email" placeholder="e.g. rahul@example.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="dark:bg-slate-800 dark:border-slate-700" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium dark:text-slate-300">Business Name *</label>
                <Input required placeholder="e.g. Sharma Traders" value={formData.business_name} onChange={e => setFormData({...formData, business_name: e.target.value})} className="dark:bg-slate-800 dark:border-slate-700" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium dark:text-slate-300">Phone Number</label>
                <Input placeholder="e.g. +91 9876543210" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="dark:bg-slate-800 dark:border-slate-700" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium dark:text-slate-300">Initial Password *</label>
              <Input required type="password" placeholder="At least 6 characters" minLength={6} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="dark:bg-slate-800 dark:border-slate-700" />
              <p className="text-xs text-slate-500">The merchant can change this later.</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium dark:text-slate-300">Module</label>
              <Select value={selectedModule} onValueChange={(v) => { setSelectedModule(v); setFormData({...formData, plan_id: 'free'}); }}>
                <SelectTrigger className="w-full dark:bg-slate-800 dark:border-slate-700">
                  <SelectValue placeholder="Select Module" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueModules.map((m: any) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium dark:text-slate-300">Initial Plan (Optional)</label>
              <Select value={formData.plan_id} onValueChange={(v) => setFormData({...formData, plan_id: v})} disabled={!selectedModule}>
                <SelectTrigger className="w-full dark:bg-slate-800 dark:border-slate-700">
                  <SelectValue placeholder={selectedModule ? "Select Plan" : "Select a Module first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">No Paid Plan (Free Tier)</SelectItem>
                  {plansForSelectedModule.map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.tier})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">If a plan is selected, a 10-year active subscription will be automatically generated.</p>
            </div>
          </form>
        </div>
        <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3 shrink-0">
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            type="submit"
            form="create-merchant-form"
            disabled={loading}
            className="bg-primary hover:bg-primary/90 text-white min-w-[140px]"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {loading ? 'Creating...' : 'Create Merchant'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
